import { createClient } from '@supabase/supabase-js';

// Validate environment configuration
const validateEnvConfig = () => {
  const requiredEnvVars = [
    'REACT_APP_SUPABASE_URL', 
    'REACT_APP_SUPABASE_ANON_KEY', 
    'REACT_APP_ENVIRONMENT'
  ];

  const missingVars = requiredEnvVars.filter(
    varName => !process.env[varName]
  );

  if (missingVars.length > 0) {
    console.error(
      'Missing required Supabase environment variables:', 
      missingVars
    );
    throw new Error('Incomplete Supabase configuration');
  }
};

// Initialize configuration validation
validateEnvConfig();

// Trim so pasted .env values with spaces/newlines don't break fetch() (TypeError: Failed to fetch)
const supabaseUrl = String(process.env.REACT_APP_SUPABASE_URL || '')
  .trim()
  .replace(/\/+$/, '');
const supabaseAnonKey = String(process.env.REACT_APP_SUPABASE_ANON_KEY || '').trim();
const environment = (process.env.REACT_APP_ENVIRONMENT || 'development').trim();

if (supabaseUrl && !/^https?:\/\//i.test(supabaseUrl)) {
  console.error(
    'REACT_APP_SUPABASE_URL must start with https:// (or http:// for local Supabase CLI)'
  );
}

// Create Supabase client with environment-specific configuration
let supabase = null;
try {
  supabase = createClient(supabaseUrl, supabaseAnonKey, {
    db: {
      schema: 'public' // Force public schema
    },
    auth: {
      persistSession: true
    }
  });

  // Test connection with comprehensive logging
  const testConnection = async () => {
    console.group('🔍 Supabase Connection Test');
    console.log('Environment:', environment);
    console.log('Schema:', environment === 'production' ? 'public' : 'dev');
    
    try {
      const { data, error } = await supabase
        .from('gallery')
        .select('*')
        .limit(1);
      
      console.log('Raw query result:', { data, error });
      
      if (error) {
        console.error('❌ Supabase connection test failed:', error);
        console.log('Error details:', {
          code: error.code,
          details: error.details,
          hint: error.hint,
          message: error.message
        });
      } else {
        console.log('✅ Supabase connection test successful');
      }
    } catch (err) {
      console.error('❌ Unexpected Supabase connection error:', err);
    } finally {
      console.groupEnd();
    }
  };

  testConnection();
} catch (err) {
  console.error('Failed to create Supabase client:', err);
}

// Export the Supabase client
export { supabase };

// Authentication Helpers
export const authHelpers = {
  signInWithGoogle: async () => {
    return await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: window.location.origin
      }
    });
  },

  signOut: async () => {
    return await supabase.auth.signOut();
  },

  getCurrentUser: () => {
    return supabase.auth.getUser();
  },

  onAuthChange: (callback) => {
    return supabase.auth.onAuthStateChange(callback);
  }
};

// Storage bucket selection
export const getStorageBucket = () => {
  return process.env.REACT_APP_STORAGE_BUCKET || 
         (environment === 'production' ? 'prod-images' : 'dev-images');
};

// Environment getter with additional logging
export const getEnvironment = () => {
  console.log(`Current Environment: ${environment}`);
  return environment;
};

// Logging configuration for debugging
console.log('Supabase Client Initialized', {
  url: supabaseUrl.replace(/\/+$/, ''),
  environment: environment,
  schema: environment === 'production' ? 'public' : 'dev',
  envVars: {
    REACT_APP_SUPABASE_URL: process.env.REACT_APP_SUPABASE_URL,
    REACT_APP_SUPABASE_ANON_KEY: process.env.REACT_APP_SUPABASE_ANON_KEY ? 'PRESENT' : 'MISSING',
    REACT_APP_ENVIRONMENT: process.env.REACT_APP_ENVIRONMENT
  }
});

// Export for external use
export default supabase;
